import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, X, Eye, EyeOff, ArrowRight, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import loginVideo from "@/assets/login-video.webm";
import Seo from "@/components/Seo";

const inputClass =
  "w-full bg-white/50 backdrop-blur-sm border border-transparent rounded-lg px-5 py-4 text-sm tracking-widest placeholder:text-muted-foreground/70 placeholder:tracking-widest placeholder:uppercase focus:outline-none focus:ring-2 focus:ring-ring/30 transition-all";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const RegisterPage = () => {
  const [form, setForm] = useState({
    firstName: "", lastName: "", company: "", phone: "", email: "", password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from;
  const fromPricing = from === "/pricing";

  const emailError =
    emailTouched && form.email.length > 0 && !EMAIL_REGEX.test(form.email)
      ? "Hmm, det där ser inte ut som en giltig e-postadress"
      : "";

  const update = (key: string, val: string) => setForm((f) => ({ ...f, [key]: val }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading || !agreed) return;
    setLoading(true);
    const { data, error } = await supabase.auth.signUp({
      email: form.email,
      password: form.password,
      options: {
        data: {
          first_name: form.firstName,
          last_name: form.lastName,
          company: form.company,
          phone: form.phone,
        },
        emailRedirectTo: window.location.origin,
      },
    });
    // Logga ut direkt så pending-konton inte får aktiv session
    await supabase.auth.signOut();
    if (error) {
      setLoading(false);
      toast.error(error.message);
      return;
    }

    // Skicka bekräftelse till sökanden + intern notis (best-effort, blockera inte UX)
    const userId = data.user?.id ?? form.email;
    try {
      await supabase.functions.invoke("submit-application", {
        body: {
          applicationId: userId,
          email: form.email,
          firstName: form.firstName,
          lastName: form.lastName,
          company: form.company,
          phone: form.phone,
        },
      });
    } catch (e) {
      console.error("Email dispatch failed", e);
    }

    setLoading(false);
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="min-h-screen grid md:grid-cols-2">
        <div className="hidden md:block">
          <video src={loginVideo} autoPlay loop muted playsInline className="w-full h-full object-cover" />
        </div>
        <div className="flex flex-col bg-gradient-to-b from-coral-400/30 via-terracotta-100/40 to-cream-100">
          <div className="flex justify-between items-center px-6 py-4">
            <Link to="/" className="p-2 rounded-full hover:bg-white/30 transition-colors">
              <ArrowLeft className="w-5 h-5 text-foreground" />
            </Link>
            <Link to="/" className="text-xl font-bold tracking-wide text-foreground">aQuadias</Link>
            <Link to="/" className="p-2 rounded-full hover:bg-white/30 transition-colors">
              <X className="w-5 h-5 text-foreground" />
            </Link>
          </div>
          <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
            <div className="w-full max-w-md space-y-6">
              <div>
                <span className="font-serif italic text-5xl md:text-6xl text-foreground">Tack</span>
                <h1 className="text-2xl md:text-3xl font-bold uppercase tracking-wider text-foreground mt-2">
                  för din ansökan!
                </h1>
              </div>
              <p className="text-sm text-foreground/80 leading-relaxed">
                Vi har tagit emot din begäran om att bli kund hos aQuadias. Vårt team granskar din ansökan manuellt och hör av sig inom 24 timmar.
              </p>
              <p className="text-xs uppercase tracking-widest text-foreground/70 bg-white/40 backdrop-blur-sm rounded-lg px-4 py-3">
                När ditt konto är godkänt kan du logga in och börja beställa
              </p>
              <Link
                to="/"
                className="inline-flex items-center justify-center gap-2 w-full border border-foreground text-foreground bg-transparent py-3.5 rounded-lg font-medium uppercase tracking-widest text-sm hover:bg-foreground hover:text-background transition-all duration-300"
              >
                Tillbaka till startsidan <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <Seo title="Bli kund – Aquadias" description="Ansök om att bli företagskund hos aQuadias och få vitamindispenser till arbetsplatsen." path="/register" />
      <div className="hidden md:block">
        <video src={loginVideo} autoPlay loop muted playsInline className="w-full h-full object-cover" />
      </div>

      <div className="flex flex-col bg-gradient-to-b from-coral-400/30 via-terracotta-100/40 to-cream-100">
        <div className="flex justify-between items-center px-6 py-4">
          <Link to={from ? "/login" : "/login"} state={from ? { from } : undefined} className="p-2 rounded-full hover:bg-white/30 transition-colors">
            <ArrowLeft className="w-5 h-5 text-foreground" />
          </Link>
          <Link to="/" className="text-xl font-bold tracking-wide text-foreground">aQuadias</Link>
          <Link to="/" className="p-2 rounded-full hover:bg-white/30 transition-colors">
            <X className="w-5 h-5 text-foreground" />
          </Link>
        </div>

        <div className="flex-1 flex flex-col items-center justify-center px-6 py-6">
          <div className="w-full max-w-md space-y-6 text-center">
            <div>
              <span className="font-serif italic text-5xl md:text-6xl text-foreground">Välkommen</span>
              <h1 className="text-2xl md:text-3xl font-bold uppercase tracking-wider text-foreground mt-2">
                skapa konto
              </h1>
            </div>

            {fromPricing && (
              <p className="text-xs uppercase tracking-widest text-foreground/80 bg-white/40 backdrop-blur-sm rounded-lg px-4 py-3">
                Skapa konto för att kunna beställa. Vi godkänner manuellt inom 24h.
              </p>
            )}

            <form onSubmit={handleSubmit} className="space-y-3 text-left">
              <input type="text" value={form.firstName} onChange={(e) => update("firstName", e.target.value)} placeholder="FÖRNAMN" className={inputClass} required />
              <input type="text" value={form.lastName} onChange={(e) => update("lastName", e.target.value)} placeholder="EFTERNAMN" className={inputClass} required />
              <input type="text" value={form.company} onChange={(e) => update("company", e.target.value)} placeholder="FÖRETAG" className={inputClass} />
              <input type="tel" value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="TELEFONNUMMER" className={inputClass} />
              <div>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  onBlur={() => setEmailTouched(true)}
                  placeholder="E-POSTADRESS"
                  aria-invalid={!!emailError}
                  aria-describedby={emailError ? "register-email-error" : undefined}
                  className={`${inputClass} ${emailError ? "border-destructive/60" : ""}`}
                  required
                />
                {emailError && (
                  <p id="register-email-error" className="mt-2 px-2 text-xs text-destructive/90">
                    {emailError}
                  </p>
                )}
              </div>
              <div className="relative">
                <input type={showPassword ? "text" : "password"} value={form.password} onChange={(e) => update("password", e.target.value)} placeholder="LÖSENORD" className={`${inputClass} pr-12`} required minLength={8} />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground transition-colors" aria-label={showPassword ? "Dölj lösenord" : "Visa lösenord"}>
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>

              <label className="flex items-center gap-3 cursor-pointer pt-1">
                <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="w-4 h-4 accent-foreground rounded cursor-pointer" />
                <span className="text-sm text-muted-foreground">
                  Jag godkänner{" "}
                  <Link to="/terms" className="uppercase font-semibold tracking-wider text-foreground underline">aQuadias villkor</Link>
                </span>
              </label>
            </form>
          </div>
        </div>

        <div className="px-6 pb-8 w-full max-w-md mx-auto">
          <button
            type="submit"
            onClick={handleSubmit as any}
            disabled={!agreed || loading}
            className="w-full border border-foreground text-foreground bg-transparent py-3.5 rounded-lg font-medium uppercase tracking-widest text-sm hover:bg-foreground hover:text-background transition-all duration-300 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Skicka ansökan <ArrowRight className="w-4 h-4" /></>}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
